import React, { useState } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';

export default function DistributionAdminPage() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [dryRunState, setDryRunState] = useState(true);

  const triggerDistribution = async (dryRun) => {
    if (!dryRun) {
      const confirmRun = window.confirm(
        "Are you sure you want to run the LIVE distribution? This will create recipient folders and copy report files in OneDrive."
      );
      if (!confirmRun) return;
    }

    setRunning(true);
    setDryRunState(dryRun);
    try {
      const res = await adminApi.runDistribution({ dryRun });
      setResults(res.data.results);
      if (dryRun) {
        toast.success("Dry run simulation complete!");
      } else {
        toast.success("Live report distribution complete!");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Report distribution failed");
    } finally {
      setRunning(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: '#E6FAF5', color: '#05CD99' }}>SUCCESS</span>;
      case 'partial':
        return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: '#FFF9E6', color: '#FFB800' }}>PARTIAL</span>;
      case 'skipped':
        return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: '#F1F5F9', color: '#64748B' }}>SKIPPED</span>;
      default:
        return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, fontWeight: 700, background: '#FDEFEF', color: '#EE5D50' }}>ERROR</span>;
    }
  };

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div className="page-title">Report Distribution</div>
            <div className="page-subtitle">Distribute reports from active Email Flows to email-specific OneDrive folders</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => triggerDistribution(true)} 
              disabled={running}
            >
              {running && dryRunState ? 'Simulating…' : 'Run Dry Run (Preview)'}
            </button>
            <button 
              className="btn btn-primary" 
              onClick={() => triggerDistribution(false)} 
              disabled={running}
            >
              {running && !dryRunState ? 'Distributing…' : 'Run Live Distribution'}
            </button>
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Info card */}
        <div className="card card-pad" style={{ marginBottom: 24, background: 'var(--bg-card)' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700 }}>⚙️ How Report Distribution works:</h3>
          <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: 13 }}>
            <li>The system reads all active <strong>Email Flows</strong> configurations.</li>
            <li>For each flow, it locates the corresponding report file (Excel/PDF) in your OneDrive.</li>
            <li>It extracts all recipient email addresses (internal & external) listed in the flow.</li>
            <li>It creates a folder for each email address under <code>/Email-Distribution/email@domain.com/</code> on OneDrive.</li>
            <li>It copies the resolved report file into each recipient's folder (overwriting old versions to ensure they always see the latest report).</li>
          </ol>
        </div>

        {/* Results summary stats */}
        {results && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            {[
              { label: 'Flows processed', val: results.processedFlows, color: 'var(--accent)' },
              { label: 'Files distributed', val: results.successfulCopies, color: 'var(--success)' },
              { label: 'Copy failures', val: results.failedCopies, color: results.failedCopies > 0 ? 'var(--danger)' : 'var(--text-secondary)' },
              { label: 'Run mode', val: dryRunState ? 'SIMULATION' : 'LIVE RUN', color: dryRunState ? '#7C3AED' : '#F97316' },
            ].map(s => (
              <div key={s.label} className="stat-card" style={{ padding: '14px 16px' }}>
                <div className="stat-label">{s.label}</div>
                <div className="stat-val" style={{ fontSize: 20, color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Detailed flow results table */}
        {results && (
          <div className="card">
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                {dryRunState ? 'Simulation Preview Details' : 'Distribution Run Details'}
              </h3>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Email Flow Name</th>
                    <th>Report File</th>
                    <th>Status</th>
                    <th>Distribution Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.details.map((detail, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{detail.flowName}</span>
                      </td>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: detail.fileName ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {detail.fileName || '—'}
                        </span>
                      </td>
                      <td>{getStatusBadge(detail.status)}</td>
                      <td>
                        {detail.status === 'skipped' && (
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            {detail.message}
                          </span>
                        )}
                        {detail.status === 'error' && (
                          <span style={{ fontSize: 12, color: 'var(--danger)', fontWeight: 500 }}>
                            ⚠️ {detail.message}
                          </span>
                        )}
                        {(detail.status === 'success' || detail.status === 'partial') && detail.info && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {detail.info.copiedTo.length > 0 && (
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                <strong>Distributed to:</strong>{' '}
                                {detail.info.copiedTo.map(c => c.email).join(', ')}
                              </div>
                            )}
                            {detail.info.errors.length > 0 && (
                              <div style={{ fontSize: 12, color: 'var(--danger)' }}>
                                <strong>Failed for:</strong>{' '}
                                {detail.info.errors.map(e => `${e.email} (${e.error})`).join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {results.details.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-secondary)' }}>
                        No active email flows found to process.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
